import { toJS } from 'mobx';
import { flow } from 'mobx-state-tree';
import * as GeoJSON from 'geojson';
import { PickInfo } from 'deck.gl';
import getAppRoot from '../../utils/getAppRoot';
import {
  RouteDtoModelType, MapMarkerType, VisitDtoModelType, RouteTimelineDtoModelType,
} from '../../models';
import { RouteDriverLocation } from '../../DriverLocation';
import queryRouteLayer from '../../../../components/map/utils/queryRouteLayer';
import { LayerIds } from '../../../../components/map/constants';
import DeckGL from '@deck.gl/react';
import { notification } from '@routific/hive';
import { v4 as uuidv4 } from 'uuid';
import { assignStopsFailedNotification } from '../../../../notifications/scheduling/assignStops';
import { logglyLogger } from '../../../../services/logglyLogger';
import {
  getMarkerSelectionIds,
  getNextSelectedMarkers,
  hasMultiSelectModifier,
} from '../helpers/markerSelection';

export const FALLBACK_LNGLAT = [0, 0];
export const ZOOM_THRESHOLD_FOR_MARKERS = 8;

const uniqueById = <T extends { id?: string }>(items: T[]): T[] => (
  Array.from(new Map(items.filter((item) => item?.id).map((item) => [item.id, item])).values())
);

const getSelectedItemsForMarkerMultiSelect = (self: any, ui: any) => ({
  stops: uniqueById([
    ...(self.selectedItems?.stops ?? []),
    ...(ui.selectedRouteEvent?.isStopEvent ? [ui.selectedRouteEvent] : []),
    ...(ui.selectedVisit?.timeline?.isStopEvent ? [ui.selectedVisit.timeline] : []),
  ]),
  unscheduledOrders: uniqueById([
    ...(self.selectedItems?.unscheduledOrders ?? []),
    ...(ui.selectedVisit && !ui.selectedVisit.isScheduled ? [ui.selectedVisit] : []),
  ]),
});

export const views = (self: any) => ({
  get draggedOrders(): VisitDtoModelType[] {
    const marker = self.draggingVisit?.properties;
    if (!marker?.id) return [];

    const { model } = getAppRoot(self);
    if (marker.type === 'delivering_stop') {
      return model.routeTimelineDtos.get(marker.id)?.orders ?? [];
    }
    if (marker.type === 'unscheduled') {
      const order = model.visitDtos.get(marker.id);
      return order ? [order] : [];
    }
    return [];
  },
  get routes() {
    const appRoot = getAppRoot(self);
    if (appRoot.model.isWorkspaceRouteLoading) return [];
    const { sortedRoutes } = appRoot;

    return sortedRoutes
      .filter((d: RouteDtoModelType) => d.polylineArray?.length > 0)
      .map((d: RouteDtoModelType) => (
        {
          ...d, color: toJS(d.color), polylineArray: toJS(d.polylineArray),
        }));
  },
  get startEndLayerData() {
    const appRoot = getAppRoot(self);
    if (appRoot.model.isWorkspaceRouteLoading) return [];
    const { sortedRoutes } = appRoot;
    // @ts-ignore
    return GeoJSON.parse(sortedRoutes.map((route: RouteDtoModelType) => [
      ...route?.endMarker ? [route.endMarker] : [],
      ...route?.startMarker ? [route?.startMarker] : [],
    ]).flat(), { Point: ['latitude', 'longitude'] })?.features;
  },
  get visitsIconLayerData() {
    const appRoot = getAppRoot(self);
    if (appRoot.model.isWorkspaceRouteLoading) return [];
    const { selectedRoute } = appRoot.ui;

    let allStops: any[] = [];
    const { sortedRoutes, mapView } = appRoot;

    sortedRoutes.forEach((route) => allStops.push(...Object.values(route.stops || {})));

    let stops = allStops;
    if (!mapView.toggledViewStopIcons || mapView.isZoomBeyondThreshold()) {
      stops = selectedRoute?.stops && !self.lassoMode
        ? Object.values(selectedRoute?.stops || {})
        : [];
    }
    // @ts-ignore
    return GeoJSON.parse(stops, { Point: ['latitude', 'longitude'] })?.features;
  },
  get visitsScatterplotLayerData() {
    const appRoot = getAppRoot(self);
    if (appRoot.model.isWorkspaceRouteLoading) return [];
    const { selectedRoute } = appRoot.ui;
    const { sortedRoutes } = appRoot;
    // @ts-ignore
    return GeoJSON.parse(sortedRoutes
      .filter((r) => (r.id !== selectedRoute?.id))
      .map((r) => (r.stops ? Object.values(r.stops) : []))
      .flat(), { Point: ['latitude', 'longitude'] })?.features;
  },
  get pendingVisits() {
    const { selectedRoute } = getAppRoot(self).ui;
    const { visits } = getAppRoot(self);
    // @ts-ignore
    return GeoJSON.parse(visits
      .filter((v) => v.isBeingModified && !(v.route != null && v.route.id === selectedRoute?.id))
      .map((v) => (v.marker))
      .flat(), { Point: ['latitude', 'longitude'] })?.features;
  },
  get unscheduledVisitLayerData() {
    const { unscheduledVisits } = getAppRoot(self);
    const unscheduledVisitsWithLngLat = unscheduledVisits.filter((v) => v.hasLngLat).map((v) => v.marker);
    // @ts-ignore
    return GeoJSON.parse(unscheduledVisitsWithLngLat, { Point: ['latitude', 'longitude'] })?.features;
  },
  get activeRoute(): RouteDtoModelType | undefined {
    // Different from ui.selectedRoute when an order + route is active on the map, but the route is not in "selected" mode
    const { selectedRoute, selectedVisit } = getAppRoot(self).ui;
    return selectedRoute || selectedVisit?.route;
  },
  get selectedScatterplotStops(): RouteTimelineDtoModelType[] {
    const { selectedVisit } = getAppRoot(self).ui;
    return self.selectedItems?.stops || (selectedVisit?.timeline ? [selectedVisit.timeline] : undefined);
  },
  get visitsIconLayerVisible() {
    return (self.toggledViewStopIcons && !self.isZoomBeyondThreshold() || !!self.activeRoute) && (!self.lassoMode && !self.draggingVisit);
  },
  get visitsScatterplotLayerVisible() {
    return (!!self.visitsScatterplotLayerData && !self.toggledViewStopIcons) || self.isInSelectionMode() || self.isZoomBeyondThreshold();
  },
});

export const actions = (self: any) => ({
  handleRouteClick(clicked: PickInfo<RouteDtoModelType>) {
    if (!self.isLassoMode && !self.isDrawMode) {
      const { selectRoute, selectRouteEvent } = getAppRoot(self).ui;

      if (clicked.object?.id) {
        self.setSkipNextRoutePan(true);
        selectRoute(clicked.object?.id);
        selectRouteEvent(null);
      }
    }
  },
  handleRouteHover(hovered: PickInfo<RouteDtoModelType>) {
    const { hoveredDriver, hoverRoute } = getAppRoot(self).ui;
    const { isDrawMode } = getAppRoot(self).mapView;

    if (isDrawMode) {
      return;
    }

    if (self.toggledViewMapPopup) {
      return;
    }

    if (!hoveredDriver) {
      self.setHoveredLngLat(hovered.object ? hovered.coordinate : null);
      hoverRoute(hovered.object?.id);
    }
  },
  handleDriverHover(hovered: PickInfo<RouteDriverLocation>) {
    const { hoverMarker } = self;
    const { hoverDriver } = getAppRoot(self).ui;
    const { sortedRoutes } = getAppRoot(self);
    const marker = hovered?.object;

    if (marker && self.toggledViewMapPopup) {
      return;
    }

    const driverRoute = hovered.object || null;
    hoverDriver(driverRoute);

    const routeMeta = sortedRoutes.find((r) => r.id === driverRoute?.routeId) || null;

    self.setHoveredLngLat(routeMeta ? hovered.coordinate : null);
    hoverMarker(marker);
  },
  handleDriverClick(clicked: PickInfo<RouteDriverLocation>) {
    const driverRoute = clicked.object || null;
    const { selectRoute, selectedRoute } = getAppRoot(self).ui;

    if (!selectedRoute && !self.isLassoMode) {
      selectRoute(driverRoute?.routeId);
    }
  },
  handleMarkerHover(hovered: PickInfo<MapMarkerType>) {
    const { hoverMarker } = self;
    const { hoverVisit, hoverRouteEvent } = getAppRoot(self).ui;
    const { isDrawMode } = getAppRoot(self).mapView;

    const marker = hovered?.object?.properties;
    if (isDrawMode) {
      return;
    }

    if (marker && self.toggledViewMapPopup) {
      return;
    }

    if (marker) {
      if (marker?.type === 'unscheduled') {
        hoverVisit(marker.id);
      } else if (marker.cluster) {
        hoverRouteEvent(marker.itemsIds[0], true);
      } else {
        hoverRouteEvent(marker.id, true);
      }
    } else {
      hoverVisit(undefined);
      hoverRouteEvent(undefined);
    }

    hoverMarker(marker);
  },
  handleMarkerClick(clicked: PickInfo<MapMarkerType>, event?: any) {
    const { selectMarkers, isDrawMode } = self;
    const {
      selectRouteEvent, selectedRouteEvent, selectedVisit,
    } = getAppRoot(self).ui;

    if (isDrawMode) {
      return;
    }

    const clickedMarker = clicked?.object?.properties;

    if (!clickedMarker) {
      return;
    }

    const selectedMarkerId = getMarkerSelectionIds(clickedMarker, false)[0];

    if (!selectedMarkerId) {
      return;
    }

    if (clickedMarker.type === 'delivering_stop' && hasMultiSelectModifier(event)) {
      const nextSelectedMarkers = getNextSelectedMarkers(
        clickedMarker,
        getMarkerSelectionIds(clickedMarker),
        getSelectedItemsForMarkerMultiSelect(self, { selectedRouteEvent, selectedVisit }),
      );

      selectRouteEvent(undefined);
      selectMarkers(nextSelectedMarkers);
      return;
    }

    selectMarkers(undefined);
    self.setSelectedItems({ stops: [], unscheduledOrders: [] });
    selectRouteEvent(selectedMarkerId);
  },
  handleOrderMarkerClick(clicked: PickInfo<MapMarkerType>, event?: any) {
    const { selectMarkers, isDrawMode } = self;
    const {
      selectRouteEvent, selectedRouteEvent, selectedVisit, selectVisit,
    } = getAppRoot(self).ui;

    if (isDrawMode) {
      return;
    }

    const clickedMarker = clicked?.object?.properties;

    if (!clickedMarker) {
      return;
    }

    const selectedMarkerId = getMarkerSelectionIds(clickedMarker, false)[0];

    if (!selectedMarkerId) {
      return;
    }

    if (hasMultiSelectModifier(event)) {
      const nextSelectedMarkers = getNextSelectedMarkers(
        clickedMarker,
        getMarkerSelectionIds(clickedMarker),
        getSelectedItemsForMarkerMultiSelect(self, { selectedRouteEvent, selectedVisit }),
      );

      selectRouteEvent(undefined);
      selectMarkers(nextSelectedMarkers);
      return;
    }

    selectMarkers([clickedMarker]);
    selectVisit(selectedMarkerId);
  },
  handleOrderMarkerHover(hovered: PickInfo<MapMarkerType>) {
    const { hoverMarker, hoveredMarker, isDrawMode } = self;
    const { hoverVisit, hoverRouteEvent } = getAppRoot(self).ui;

    if (isDrawMode) {
      return;
    }

    const marker = hovered?.object?.properties;
    const queryOrder = !hoveredMarker || hoveredMarker?.id !== marker?.id;

    hoverMarker(marker);

    if (marker) {
      hoverVisit(marker.id, queryOrder);
    } else {
      hoverVisit(undefined);
      hoverRouteEvent(undefined);
    }
  },
  handleVisitDragStart(dragged: PickInfo<MapMarkerType>) {
    const { hoverVisit } = getAppRoot(self).ui;
    const { canDragMarker } = self;

    if (canDragMarker(dragged.object)) {
      self.setIsDraggable(false);
      self.dragVisit(dragged.object);
      hoverVisit(undefined);
    }
  },
  handleVisitDragging(deckRef: DeckGL, dragging: PickInfo<MapMarkerType>) {
    const {
      hoverRoute, hoveredRoute,
    } = getAppRoot(self).ui;
    const { canDragMarker } = self;

    if (canDragMarker(dragging.object)) {
      const pickedTarget = queryRouteLayer(deckRef, dragging, [LayerIds.ROUTES_POLYLINES, LayerIds.START_END, LayerIds.ROUTES_PRIMARY]);
      const targetRoute = pickedTarget?.layer.props.id === LayerIds.START_END ? getAppRoot(self).model.routeDtos.get(pickedTarget.object.properties.routeId) : pickedTarget?.object;
      self.setHoveredLngLat(dragging.object ? dragging.coordinate : null);

      if (self.isStopAssignableToRoute(dragging.object, targetRoute)) {
        hoverRoute(targetRoute.id);
      } else if (!targetRoute && hoveredRoute) {
        hoverRoute(undefined);
      }

      self.dragVisit(dragging.object);
    }
  },
  handleVisitDragEnd(deckRef: DeckGL, dragged: PickInfo<MapMarkerType>) {
    const { hoverRoute, hoverVisit } = getAppRoot(self).ui;
    const { canDragMarker } = self;

    if (canDragMarker(dragged.object)) {
      self.setIsDraggable(true);
      hoverVisit(undefined);
      hoverRoute(undefined);

      const pickedTarget = queryRouteLayer(deckRef, dragged, [LayerIds.ROUTES_POLYLINES, LayerIds.START_END, LayerIds.ROUTES_PRIMARY]);
      const targetRoute = pickedTarget?.layer.props.id === LayerIds.START_END ? getAppRoot(self).model.routeDtos.get(pickedTarget.object.properties.routeId) : pickedTarget?.object;

      if (self.isStopAssignableToRoute(dragged.object, targetRoute)) {
        self.dragVisitsToTargetRoute(dragged.object, targetRoute);
      } else {
        self.dragVisit(undefined);
      }
    }
  },
  getOrderUuids() {
    const orders = self.draggedOrders;

    // Check if the dragged item is part of the current selection (lasso or table-based)
    const selectedOrders = self.ordersWithinLasso?.length ? self.ordersWithinLasso : orders;
    
    // For scheduled stops (timeline events), we need to check if any of the event's orders are in the selection
    // For unscheduled orders, we check the order ID directly
    const isDraggedItemInSelection = orders.some((draggedOrder: VisitDtoModelType) =>
      selectedOrders.some((selectedOrder: VisitDtoModelType) => 
        selectedOrder.id === draggedOrder.id,
      ),
    );

    // If dragged item is in the selection, drag all selected items; otherwise just the dragged item
    const draggedVisits = isDraggedItemInSelection && selectedOrders.length > 0
      ? selectedOrders
      : orders;

    return draggedVisits.filter((visit: VisitDtoModelType) => visit.uuid).map((visit: VisitDtoModelType) => visit.uuid!);
  },
  // Same move as a Gantt drag (Timeline.dragVisitsToTargetRoute): a best insert
  // with no re-optimize decision, and route updates arrive over the subscriptions.
  dragVisitsToTargetRoute: flow(function* (draggedMarker: MapMarkerType, targetRoute: RouteDtoModelType | undefined) {
    const orderUuids = self.getOrderUuids();
    if (!orderUuids.length || !targetRoute?.uuid) {
      self.dragVisit(undefined);
      return;
    }
    const root = getAppRoot(self);

    try {
      yield root.model.moveOrdersToRoutes(orderUuids, [targetRoute.uuid], false);
      self.setSelectedItems(undefined);
      self.clearSelectedStopAnchorsByRoute();
      self.setLassoMode(null);
      self.exitDrawMode();
      root.ui.selectRouteEvent(undefined);
    } catch (err: any) {
      notification(assignStopsFailedNotification(uuidv4(), orderUuids.length));
      logglyLogger.log({ error: 'moveOrdersToRoutes Failed', info: err?.message });
    } finally {
      self.dragVisit(undefined);
    }
  }),
});
